import React from 'react';

import { addApiService } from '@mygpt/app/functions/services/add';
import { batchDeleteApiService, deleteApiService } from '@mygpt/app/functions/services/delete';
import { updateApiServiceFunction } from '@mygpt/app/functions/services/update';
import { apisStoreFunction } from '@mygpt/app/functions/store';

export const useAPIsMethods = (datasetId: string) => {
  const [loading, setLoading] = React.useState(false);

  const add = React.useCallback(
    async (params: Parameters<typeof addApiService>[0]) => {
      setLoading(true);
      return addApiService(params)
        .then((res) => {
          const data = apisStoreFunction.getState().records[datasetId] ?? [];
          const newData = [res.data, ...data];
          apisStoreFunction.setState({
            records: {
              [datasetId]: newData,
            },
            cacheTotal: {
              [datasetId]: (apisStoreFunction.getState().cacheTotal[datasetId] ?? 0) + 1,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [datasetId]
  );

  const batchDelete = React.useCallback(
    async (params: Parameters<typeof batchDeleteApiService>[0]) => {
      setLoading(true);
      return batchDeleteApiService(params)
        .then(() => {
          const data = apisStoreFunction.getState().records[datasetId] ?? [];
          const newData = data.concat();
          params.apiIds.forEach((id) => {
            const index = newData.findIndex((item) => item.id === id);
            if (index > -1) {
              newData.splice(index, 1);
            }
          });
          apisStoreFunction.setState({
            records: {
              [datasetId]: newData,
            },
            cacheTotal: {
              [datasetId]: (apisStoreFunction.getState().cacheTotal[datasetId] ?? 0) - params.apiIds.length,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [datasetId]
  );

  const update = React.useCallback(
    async (params: Parameters<typeof updateApiServiceFunction>[0]) => {
      setLoading(true);
      return updateApiServiceFunction(params)
        .then((res) => {
          const data = apisStoreFunction.getState().records[datasetId] ?? [];
          const newData = data.concat();
          const item = newData.find((item) => item.id === res.data.id);
          if (item) {
            Object.assign(item, res.data);
            apisStoreFunction.setState({
              records: {
                [datasetId]: newData,
              },
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [datasetId]
  );

  const _delete = React.useCallback(
    async (params: Parameters<typeof deleteApiService>[0]) => {
      setLoading(true);
      return deleteApiService(params)
        .then((res) => {
          const data = apisStoreFunction.getState().records[datasetId] ?? [];
          const newData = data.concat();
          const index = newData.findIndex((item) => item.id === params.apiId);
          if (index > -1) {
            newData.splice(index, 1);
            apisStoreFunction.setState({
              records: {
                [datasetId]: newData,
              },
              cacheTotal: {
                [datasetId]: (apisStoreFunction.getState().cacheTotal[datasetId] ?? 0) - 1,
              },
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [datasetId]
  );

  return {
    loading,
    addAPIs: add,
    updateAPIs: update,
    deleteAPIs: _delete,
    batchDeleteAPIs: batchDelete,
  };
};
